/** Baselines & Profitability — city × designation baselines + profitability
 *  dashboard (brand / project / package) + on-demand AI seed. */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sparkles, Plus, Trash2, Save, MapPin, TrendingUp, TrendingDown, Loader2, LineChart as LineChartIcon } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar,
} from "recharts";

const fmt = (v) => v == null ? "—" : `₹${Number(v).toLocaleString("en-IN")}`;

/**
 * MarginSparkline — R148g · Feb 2026
 * Tiny inline SVG line chart showing rolling monthly margin_pct.
 * Green when trending up (or ≥25%), amber for flat/mid, red for
 * decaying or <10% margin — designed so PMs spot decay at a glance.
 */
function MarginSparkline({ spark, trend, months }) {
  if (!spark || spark.length < 2) {
    return <span className="text-[10px] text-muted-foreground">—</span>;
  }
  // Restrict to the tail-N months so the sparkline is always "rolling".
  const data = spark.slice(-Math.max(2, months || 6));
  const values = data.map((d) => (d.margin_pct == null ? 0 : d.margin_pct));
  const hasSignal = values.some((v) => v !== 0);
  if (!hasSignal) {
    return <span className="text-[10px] text-muted-foreground">flat</span>;
  }
  const W = 96, H = 28, PAD = 2;
  const min = Math.min(...values, 0);
  const max = Math.max(...values, 20);
  const range = Math.max(max - min, 1);
  const step = (W - PAD * 2) / (data.length - 1);
  const y = (v) => H - PAD - ((v - min) / range) * (H - PAD * 2);
  const pts = data.map((d, i) => `${PAD + i * step},${y(d.margin_pct ?? 0)}`).join(" ");
  const lastVal = values[values.length - 1];
  const stroke = trend != null
    ? (trend >= 3 ? "#10b981" : trend <= -3 ? "#ef4444" : "#f59e0b")
    : (lastVal >= 25 ? "#10b981" : lastVal < 10 ? "#ef4444" : "#f59e0b");
  const tip = data.map((d) => `${d.month}: ${d.margin_pct}%`).join("\n");
  return (
    <svg width={W} height={H} className="inline-block align-middle" data-testid="margin-sparkline"
         role="img" aria-label={`Margin ${lastVal}%, trend ${trend ?? 0} pp`}>
      <title>{tip}</title>
      {/* Baseline */}
      <line x1={PAD} y1={y(0)} x2={W - PAD} y2={y(0)} stroke="var(--border)" strokeDasharray="2 2" />
      <polyline points={pts} fill="none" stroke={stroke} strokeWidth="1.5" />
      {/* Last-point dot */}
      <circle cx={PAD + (data.length - 1) * step} cy={y(lastVal)} r="1.8" fill={stroke} />
    </svg>
  );
}

export default function BaselinesAndProfitability({ wsId }) {
  return (
    <Tabs defaultValue="baselines" className="w-full" data-testid="rai-tabs">
      <TabsList className="rounded-none">
        <TabsTrigger value="baselines" className="rounded-none" data-testid="rai-tab-baselines"><MapPin className="w-3.5 h-3.5 mr-1.5" />City baselines</TabsTrigger>
        <TabsTrigger value="profitability" className="rounded-none" data-testid="rai-tab-profit"><LineChartIcon className="w-3.5 h-3.5 mr-1.5" />Profitability</TabsTrigger>
      </TabsList>
      <TabsContent value="baselines" className="pt-4"><BaselinesTab wsId={wsId} /></TabsContent>
      <TabsContent value="profitability" className="pt-4"><ProfitabilityTab wsId={wsId} /></TabsContent>
    </Tabs>
  );
}

// ─────────────────── Baselines ───────────────────

function BaselinesTab({ wsId }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [rateCards, setRateCards] = useState([]);
  const [analyseCard, setAnalyseCard] = useState(null);
  const [filter, setFilter] = useState({ city: "all", designation: "" });

  const load = () => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/rate-baselines`).then((r) => setData(r.data)).catch(() => setData({ rows: [], cities: [], designations: [] }));
    api.get(`/workspaces/${wsId}/rate-cards?include_unpublished=false`).then((r) => setRateCards(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId]);

  const seed = async () => {
    setSeeding(true);
    try {
      const { data: r } = await api.post(`/workspaces/${wsId}/rate-baselines/ai-seed`, { overwrite: false });
      toast.success(`Seed complete: ${r.inserted} inserted, ${r.updated} updated`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "AI seed failed")); }
    finally { setSeeding(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete baseline "${b.designation} · ${b.city}"?`)) return;
    try {
      await api.delete(`/rate-baselines/${b.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const shown = useMemo(() => {
    if (!data) return [];
    return data.rows.filter((r) => {
      if (filter.city !== "all" && r.city !== filter.city) return false;
      if (filter.designation && !r.designation.toLowerCase().includes(filter.designation.toLowerCase())) return false;
      return true;
    });
  }, [data, filter]);

  return (
    <div className="space-y-3" data-testid="baselines-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-sm text-muted-foreground flex-1">
          Industry rate baselines per city × designation. Compare your rate card against these to spot outliers.
        </div>
        <Select value={filter.city} onValueChange={(v) => setFilter((f) => ({ ...f, city: v }))}>
          <SelectTrigger className="h-8 w-40 rounded-none text-xs" data-testid="bl-filter-city"><SelectValue placeholder="City" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All cities</SelectItem>
            {(data?.cities || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input value={filter.designation} onChange={(e) => setFilter((f) => ({ ...f, designation: e.target.value }))} placeholder="Find designation…" className="h-8 w-48 rounded-none text-xs" data-testid="bl-filter-designation" />
        <Button variant="outline" className="rounded-none h-8" onClick={seed} disabled={seeding} data-testid="bl-ai-seed">
          {seeding ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
          AI seed baselines
        </Button>
        <Button className="rounded-none h-8" onClick={() => setEditing({})} data-testid="bl-new"><Plus className="w-3.5 h-3.5 mr-1" />New</Button>
      </div>

      {rateCards.length > 0 && (
        <div className="border p-3 bg-muted/20" data-testid="bl-analyse-strip">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Analyse a rate card</span>
            <Select value={analyseCard?.id || ""} onValueChange={(id) => setAnalyseCard(rateCards.find((c) => c.id === id) || null)}>
              <SelectTrigger className="h-8 w-64 rounded-none text-xs" data-testid="bl-analyse-select"><SelectValue placeholder="Pick a rate card…" /></SelectTrigger>
              <SelectContent className="max-h-64">
                {rateCards.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · v{c.version}</SelectItem>)}
              </SelectContent>
            </Select>
            {analyseCard && (
              <AnalyseCardTrigger wsId={wsId} card={analyseCard} cities={data?.cities || []} onClose={() => setAnalyseCard(null)} />
            )}
          </div>
        </div>
      )}

      {!data ? <div className="text-sm text-muted-foreground p-6">Loading…</div>
        : shown.length === 0 ? (
          <div className="border p-8 text-center text-sm text-muted-foreground" data-testid="bl-empty">
            <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
            No baselines yet — click <b>AI seed baselines</b> to generate a starter grid.
          </div>
        ) : (
          <div className="border divide-y" data-testid="bl-list">
            <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              <div className="col-span-2">City</div>
              <div className="col-span-3">Designation</div>
              <div className="col-span-1">Category</div>
              <div className="col-span-1 text-right">Min</div>
              <div className="col-span-1 text-right">Median</div>
              <div className="col-span-1 text-right">Max</div>
              <div className="col-span-2">Source</div>
              <div className="col-span-1 text-right"></div>
            </div>
            {shown.map((b) => (
              <div key={b.id} className="grid grid-cols-12 gap-2 px-3 py-2 items-center text-xs" data-testid={`bl-row-${b.id}`}>
                <button onClick={() => setEditing(b)} className="col-span-2 text-left font-semibold hover:text-primary" data-testid={`bl-edit-${b.id}`}>{b.city}</button>
                <div className="col-span-3">{b.designation}</div>
                <div className="col-span-1">{b.category && <Badge variant="outline" className="rounded-none text-[9px]">{b.category}</Badge>}</div>
                <div className="col-span-1 text-right font-mono">{fmt(b.min_rate)}</div>
                <div className="col-span-1 text-right font-mono font-bold">{fmt(b.median_rate)}</div>
                <div className="col-span-1 text-right font-mono">{fmt(b.max_rate)}</div>
                <div className="col-span-2 text-[10px] text-muted-foreground truncate">{b.source}</div>
                <div className="col-span-1 text-right">
                  <button onClick={() => remove(b)} className="text-destructive" data-testid={`bl-del-${b.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

      {editing !== null && <BaselineDialog wsId={wsId} b={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function BaselineDialog({ wsId, b, onClose, onSaved }) {
  const isNew = !b.id;
  const [form, setForm] = useState({
    city: b.city || "", designation: b.designation || "", category: b.category || "",
    min_rate: b.min_rate ?? 0, median_rate: b.median_rate ?? 0, max_rate: b.max_rate ?? 0,
    currency: b.currency || "INR", source: b.source || "manual", notes: b.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      if (isNew) await api.post(`/workspaces/${wsId}/rate-baselines`, form);
      else await api.put(`/rate-baselines/${b.id}`, form);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg rounded-none" data-testid="bl-dialog">
        <DialogHeader><DialogTitle>{isNew ? "New baseline" : "Edit baseline"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><label className="text-[10px] uppercase text-muted-foreground">City</label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="h-9 rounded-none" data-testid="bl-input-city" /></div>
          <div><label className="text-[10px] uppercase text-muted-foreground">Designation</label><Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="h-9 rounded-none" data-testid="bl-input-designation" /></div>
          <div><label className="text-[10px] uppercase text-muted-foreground">Category</label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="h-9 rounded-none" data-testid="bl-input-category" /></div>
          <div><label className="text-[10px] uppercase text-muted-foreground">Currency</label><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} className="h-9 rounded-none font-mono" /></div>
          <div><label className="text-[10px] uppercase text-muted-foreground">Min ₹/hr</label><Input type="number" value={form.min_rate} onChange={(e) => setForm({ ...form, min_rate: Number(e.target.value) })} className="h-9 rounded-none font-mono" data-testid="bl-input-min" /></div>
          <div><label className="text-[10px] uppercase text-muted-foreground">Median ₹/hr</label><Input type="number" value={form.median_rate} onChange={(e) => setForm({ ...form, median_rate: Number(e.target.value) })} className="h-9 rounded-none font-mono" data-testid="bl-input-median" /></div>
          <div className="col-span-2"><label className="text-[10px] uppercase text-muted-foreground">Max ₹/hr</label><Input type="number" value={form.max_rate} onChange={(e) => setForm({ ...form, max_rate: Number(e.target.value) })} className="h-9 rounded-none font-mono" data-testid="bl-input-max" /></div>
          <div className="col-span-2"><label className="text-[10px] uppercase text-muted-foreground">Source</label><Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="h-9 rounded-none" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="bl-save">{busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}<Save className="w-3.5 h-3.5 mr-1" />Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────── Analyse rate card vs baselines ───────────────────

function AnalyseCardTrigger({ wsId, card, cities, onClose }) {
  const [open, setOpen] = useState(true);
  const [city, setCity] = useState(cities[0] || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/rate-cards/${card.id}/analyze?${city ? `city=${encodeURIComponent(city)}` : ""}`);
      setResult(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Analysis failed")); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (open) run(); /* eslint-disable-next-line */ }, [open, city]);
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl rounded-none max-h-[92vh] overflow-y-auto" data-testid="analyse-dialog">
        <DialogHeader><DialogTitle>Analyse · {card.name} v{card.version}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase text-muted-foreground">Compare to city</span>
            <Select value={city} onValueChange={setCity}>
              <SelectTrigger className="h-8 w-56 rounded-none text-xs" data-testid="analyse-city"><SelectValue /></SelectTrigger>
              <SelectContent>{cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          </div>
          {result && (
            <>
              <div className="grid grid-cols-4 gap-2">
                <Tile label="Within range" v={result.summary.within_range} tone="text-emerald-600" />
                <Tile label="Below range" v={result.summary.below_range} tone="text-red-600" />
                <Tile label="Above range" v={result.summary.above_range} tone="text-amber-600" />
                <Tile label="No baseline" v={result.summary.missing_baseline} />
              </div>
              {result.narrative && (
                <div className="border border-violet-500/30 bg-violet-500/5 p-3 space-y-2" data-testid="analyse-narrative">
                  <div className="font-bold text-sm inline-flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-violet-500" />{result.narrative.headline}</div>
                  <p className="text-sm">{result.narrative.narrative}</p>
                  {(result.narrative.recommendations || []).length > 0 && (
                    <div><div className="text-[10px] uppercase text-violet-600">Recommendations</div><ul className="list-disc pl-5 text-xs">{result.narrative.recommendations.map((r, i) => <li key={i}>{r}</li>)}</ul></div>
                  )}
                </div>
              )}
              <div className="border overflow-x-auto" data-testid="analyse-table">
                <table className="w-full text-xs">
                  <thead className="bg-muted/40 text-left text-[10px] uppercase text-muted-foreground">
                    <tr>
                      <th className="px-3 py-1.5">Designation</th>
                      <th className="px-3 py-1.5 text-right">Our rate</th>
                      <th className="px-3 py-1.5 text-right">Min</th>
                      <th className="px-3 py-1.5 text-right">Median</th>
                      <th className="px-3 py-1.5 text-right">Max</th>
                      <th className="px-3 py-1.5 text-right">Δ vs median</th>
                      <th className="px-3 py-1.5">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.comparisons.map((c) => (
                      <tr key={c.designation} className="border-t">
                        <td className="px-3 py-1.5">{c.designation}</td>
                        <td className="px-3 py-1.5 text-right font-mono">{fmt(c.our_rate)}</td>
                        <td className="px-3 py-1.5 text-right font-mono text-muted-foreground">{fmt(c.baseline?.min_rate)}</td>
                        <td className="px-3 py-1.5 text-right font-mono">{fmt(c.baseline?.median_rate)}</td>
                        <td className="px-3 py-1.5 text-right font-mono text-muted-foreground">{fmt(c.baseline?.max_rate)}</td>
                        <td className={`px-3 py-1.5 text-right font-mono ${c.delta_vs_median_pct > 5 ? "text-emerald-600" : c.delta_vs_median_pct < -5 ? "text-red-600" : ""}`}>
                          {c.delta_vs_median_pct != null ? (c.delta_vs_median_pct > 0 ? <><TrendingUp className="w-3 h-3 inline" />+{c.delta_vs_median_pct}%</> : <><TrendingDown className="w-3 h-3 inline" />{c.delta_vs_median_pct}%</>) : "—"}
                        </td>
                        <td className="px-3 py-1.5">
                          <Badge variant="outline" className={`rounded-none text-[9px] ${c.status === "within_range" ? "text-emerald-600" : c.status === "below_range" ? "text-red-500" : c.status === "above_range" ? "text-amber-600" : "text-muted-foreground"}`}>{c.status.replace(/_/g, " ")}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Tile({ label, v, tone = "" }) {
  return (
    <div className="border p-2">
      <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
      <div className={`text-xl font-mono font-black ${tone}`}>{v ?? "—"}</div>
    </div>
  );
}

// ─────────────────── Profitability ───────────────────

function ProfitabilityTab({ wsId }) {
  const [scope, setScope] = useState("brand");
  const [range, setRange] = useState(() => {
    const t = new Date();
    const from = new Date(t); from.setMonth(t.getMonth() - 5); from.setDate(1);
    const to = new Date(t.getFullYear(), t.getMonth() + 1, 0);
    return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
  });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (!wsId) return;
    setBusy(true);
    api.get(`/workspaces/${wsId}/package-profitability?scope=${scope}&from=${range.from}&to=${range.to}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId, scope, range.from, range.to]);

  const canPrice = data && "price" in (data.totals || {}) && data.totals.price !== null;
  const canCost = data && "cost" in (data.totals || {}) && data.totals.cost !== null;

  return (
    <div className="space-y-3" data-testid="profit-tab">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="h-8 w-40 rounded-none text-xs" data-testid="profit-scope"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="brand">By brand</SelectItem>
            <SelectItem value="project">By project</SelectItem>
            <SelectItem value="package">By package</SelectItem>
          </SelectContent>
        </Select>
        <Input type="date" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className="h-8 w-36 rounded-none text-xs" data-testid="profit-from" />
        <span className="text-xs text-muted-foreground">→</span>
        <Input type="date" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className="h-8 w-36 rounded-none text-xs" data-testid="profit-to" />
        {busy && <span className="text-[11px] text-muted-foreground ml-auto">Loading…</span>}
      </div>

      {data && (
        <>
          <div className="grid grid-cols-4 gap-2" data-testid="profit-totals">
            {canPrice && <Tile label="Revenue" v={fmt(data.totals.price)} tone="text-emerald-600" />}
            {canCost && <Tile label="Cost" v={fmt(data.totals.cost)} tone="text-amber-600" />}
            {data.totals.margin_pct != null && <Tile label="Margin %" v={`${data.totals.margin_pct}%`} tone={data.totals.margin_pct < 20 ? "text-red-600" : "text-emerald-600"} />}
            <Tile label="Hours delivered" v={data.totals.hours} />
          </div>
          {data.monthly && data.monthly.length > 0 && (
            <div className="border p-3" data-testid="profit-chart">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Revenue vs cost by month</div>
              <div className="h-64">
                <ResponsiveContainer>
                  <LineChart data={data.monthly} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: "var(--background)", border: "1px solid var(--border)", fontSize: 11 }} formatter={(v) => fmt(v)} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    {canPrice && <Line type="monotone" dataKey="price" stroke="#10b981" strokeWidth={2} dot={false} name="Revenue" />}
                    {canCost && <Line type="monotone" dataKey="cost" stroke="#f59e0b" strokeWidth={2} dot={false} name="Cost" />}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          {data.rows && data.rows.length > 0 && (
            <div className="border p-3" data-testid="profit-bars">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Breakdown by {scope}</div>
              <div className="h-72">
                <ResponsiveContainer>
                  <BarChart data={data.rows.slice(0, 10)} layout="vertical" margin={{ top: 5, right: 10, left: 80, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="label" width={100} tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => fmt(v)} contentStyle={{ background: "var(--background)", border: "1px solid var(--border)", fontSize: 11 }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    {canPrice && <Bar dataKey="price" fill="#10b981" name="Revenue" />}
                    {canCost && <Bar dataKey="cost" fill="#f59e0b" name="Cost" />}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          <div className="border overflow-x-auto" data-testid="profit-table">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-left text-[10px] uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-1.5">{scope}</th>
                  <th className="px-3 py-1.5 text-right">Packages</th>
                  <th className="px-3 py-1.5 text-right">Months</th>
                  <th className="px-3 py-1.5 text-right">Hours</th>
                  {canPrice && <th className="px-3 py-1.5 text-right">Revenue</th>}
                  {canCost && <th className="px-3 py-1.5 text-right">Cost</th>}
                  {data.totals.margin_pct != null && <th className="px-3 py-1.5 text-right">Margin %</th>}
                  {data.totals.margin_pct != null && (
                    <th className="px-3 py-1.5 text-right" title="Rolling monthly margin — spot decay early">
                      Trend ({range.from.slice(0, 7)} → {range.to.slice(0, 7)})
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {(data.rows || []).map((r) => (
                  <tr key={r.scope_key} className="border-t" data-testid={`profit-row-${r.scope_key}`}>
                    <td className="px-3 py-1.5 font-semibold">{r.label}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{r.packages}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{r.months}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{r.hours}</td>
                    {canPrice && <td className="px-3 py-1.5 text-right font-mono text-emerald-700">{fmt(r.price)}</td>}
                    {canCost && <td className="px-3 py-1.5 text-right font-mono text-amber-700">{fmt(r.cost)}</td>}
                    {r.margin_pct != null && <td className={`px-3 py-1.5 text-right font-mono ${r.margin_pct < 20 ? "text-red-600" : "text-emerald-700"}`}>{r.margin_pct}%</td>}
                    {data.totals.margin_pct != null && (
                      <td className="px-3 py-1.5 text-right whitespace-nowrap" data-testid={`profit-spark-${r.scope_key}`}>
                        <MarginSparkline spark={r.spark} trend={r.margin_trend_pp} months={6} />
                        {r.margin_trend_pp != null && (
                          <span
                            className={`ml-1.5 text-[10px] font-mono font-bold ${r.margin_trend_pp >= 3 ? "text-emerald-600" : r.margin_trend_pp <= -3 ? "text-red-600" : "text-amber-600"}`}
                            title="Delta between last month's margin and the average of prior months (percentage points)"
                          >
                            {r.margin_trend_pp > 0 ? "+" : ""}{r.margin_trend_pp}pp
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                {(!data.rows || data.rows.length === 0) && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">No packages in this window.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
